import {
  Injectable,
  inject,
  Injector,
  runInInjectionContext,
  EnvironmentProviders,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  Messaging,
  getToken,
  onMessage,
  provideMessaging,
  getMessaging,
} from '@angular/fire/messaging';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { MessageService } from 'primeng/api';
import { environment } from '../../../environments/environment';
import {
  RegistrarDeviceTokenDto,
  PushNotificationPayload,
  DeviceToken,
} from '../models/notificacao.model';

/**
 * Verifica se o navegador suporta Firebase Messaging
 */
export function isMessagingSupported(): boolean {
  return (
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );
}

export function provideFirebaseMessagingIfSupported(): EnvironmentProviders[] {
  if (!isMessagingSupported()) {
    console.warn('[Firebase] Notificações não suportadas neste navegador');
    return [];
  }

  try {
    return [provideMessaging(() => getMessaging())];
  } catch (error) {
    console.warn('[Firebase] Erro ao configurar Messaging:', error);
    return [];
  }
}

@Injectable({
  providedIn: 'root',
})
export class FirebaseMessagingService {
  private http = inject(HttpClient);
  private messaging = inject(Messaging, { optional: true });
  private messageService = inject(MessageService);
  private injector = inject(Injector);
  private readonly apiUrl = `${environment.apiUrl}/firebase`;

  private messageSubject = new Subject<PushNotificationPayload>();
  public message$ = this.messageSubject.asObservable();

  private permissionStatusSubject = new BehaviorSubject<boolean>(
    this.hasPermission(),
  );
  public permissionStatus$ = this.permissionStatusSubject.asObservable();

  private listenerInitialized = false;
  private isInitializing = false;

  async requestPermissionAndRegister(): Promise<void> {
    try {
      if (!this.isSupported() || !this.messaging) {
        console.warn('Notificações não são suportadas neste navegador');
        return;
      }

      const permission = await Notification.requestPermission();

      if (permission === 'granted') {
        this.permissionStatusSubject.next(true);

        const token = await runInInjectionContext(this.injector, async () => {
          if (!this.messaging) {
            throw new Error('Firebase Messaging não disponível');
          }
          return await getToken(this.messaging, {
            vapidKey: environment.firebaseVapidKey,
          });
        });

        localStorage.setItem('fcm_token', token);

        await this.registrarDeviceToken({
          token,
          deviceType: 'web',
          userAgent: navigator.userAgent,
        }).toPromise();

        this.setupForegroundMessageListener();
      } else {
        this.permissionStatusSubject.next(false);
      }
    } catch (error) {
      console.error('Erro ao solicitar permissão de notificação:', error);
      throw error;
    }
  }

  private setupForegroundMessageListener(): void {
    if (this.listenerInitialized || !this.messaging) {
      return;
    }

    runInInjectionContext(this.injector, () => {
      if (!this.messaging) {
        return;
      }
      onMessage(this.messaging, payload => {
        const notification: PushNotificationPayload = {
          titulo: payload.notification?.title || 'Nova notificação',
          conteudo: payload.notification?.body || '',
          link: payload.data?.['link'],
        };

        this.messageSubject.next(notification);

        this.messageService.add({
          key: 'global-toast',
          severity: 'info',
          summary: notification.titulo,
          detail: notification.conteudo,
          life: 3000,
        });
      });
    });

    this.listenerInitialized = true;
  }

  reinitializeListener(): void {
    this.listenerInitialized = false;
    this.setupForegroundMessageListener();
  }

  registrarDeviceToken(dto: RegistrarDeviceTokenDto): Observable<DeviceToken> {
    return this.http.post<DeviceToken>(`${this.apiUrl}/token`, dto);
  }

  removerDeviceToken(token: string): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(`${this.apiUrl}/token`, {
      body: { token },
    });
  }

  listarDeviceTokens(): Observable<{ deviceTokens: DeviceToken[] }> {
    return this.http.get<{ deviceTokens: DeviceToken[] }>(
      `${this.apiUrl}/tokens`,
    );
  }

  isSupported(): boolean {
    return (
      'Notification' in window &&
      'serviceWorker' in navigator &&
      'PushManager' in window
    );
  }

  hasPermission(): boolean {
    if (!('Notification' in window)) {
      return false;
    }
    return Notification.permission === 'granted';
  }

  getStoredToken(): string | null {
    return localStorage.getItem('fcm_token');
  }

  async cleanup(): Promise<void> {
    const token = this.getStoredToken();

    if (token) {
      try {
        await this.removerDeviceToken(token).toPromise();
      } catch (error) {
        console.error(
          '[Notificações] Erro ao remover token do backend:',
          error,
        );
      }
    }

    localStorage.removeItem('fcm_token');
    this.listenerInitialized = false;
    this.isInitializing = false;
  }

  async initializeForAuthenticatedUser(): Promise<void> {
    if (this.isInitializing) {
      return;
    }

    if (!this.isSupported() || !this.messaging) {
      console.warn('[Notificações] Navegador não suporta notificações');
      return;
    }

    if (!this.hasPermission()) {
      console.warn('[Notificações] Permissão não concedida');
      return;
    }

    const storedToken = this.getStoredToken();

    if (storedToken) {
      this.reinitializeListener();
      return;
    }

    this.isInitializing = true;

    try {
      const token = await runInInjectionContext(this.injector, async () => {
        if (!this.messaging) {
          throw new Error('Firebase Messaging não disponível');
        }
        return await getToken(this.messaging, {
          vapidKey: environment.firebaseVapidKey,
        });
      });

      if (token) {
        localStorage.setItem('fcm_token', token);

        await this.registrarDeviceToken({
          token,
          deviceType: 'web',
          userAgent: navigator.userAgent,
        }).toPromise();

        this.setupForegroundMessageListener();
      }
    } catch (error) {
      console.error('[Notificações] Erro ao inicializar:', error);
    } finally {
      this.isInitializing = false;
    }
  }
}
